import './variables.css'
import './tailwind.css'
import './app.css'
import './dots.css'
import './pages/AiFans/Blog/blog.css'
import '@melloware/coloris/dist/coloris.css'
import {
  Component,
  createMemo,
  JSX,
  Show,
  lazy,
  onMount,
  onCleanup,
  Switch,
  Match,
  createEffect,
  createSignal,
} from 'solid-js'
import { Route, Router, useLocation, useNavigate, useParams } from '@solidjs/router'
import { MetaProvider } from '@solidjs/meta'
import './store'
import NavBar from './shared/NavBar'
import Notifications from './Toasts'
import CharacterRoutes from './pages/Character'
import ScenarioRoutes from './pages/Scenario'
import { settingStore } from './store/settings'
import { userStore } from './store/user'
import LoginPage from './pages/Login'
import HomePage from './pages/Home'
import HomePageAiFans from './pages/HomePage'
import Navigation from './Navigation'
import Loading from './shared/Loading'
import Button from './shared/Button'
import ImpersonateModal from './pages/Character/ImpersonateModal'
import ChubRoutes from './pages/Chub'
import Redirect from './shared/Redirect'
import Maintenance from './shared/Maintenance'
import CharacterChats from './pages/Character/ChatList'
import ChatDetail from './pages/Chat/ChatDetail'
import Settings, { SettingsModal } from './pages/Settings'
import ProfilePage, { ProfileModal } from './pages/Profile'
import { useCharacterBg, usePaneManager } from './shared/hooks'
import { rootModalStore } from './store/root-modal'
import { For } from 'solid-js'
import { css, getMaxChatWidth } from './shared/util'
import FAQ from './pages/AiFans/FAQ'
import CreateChatForm from './pages/Chat/CreateChatForm'
import Modal from './shared/Modal'
import { ContextProvider } from './store/context'
import MemoryGuide from './pages/Guides/Memory'
import NovelGuide from './pages/Guides/NovelAI'
import { ImageModal } from './pages/Chat/ImageModal'
import { CheckoutCancel, CheckoutSuccess } from './pages/Profile/Checkout'
import { markdown } from './shared/markdown'
import SoundsPage from './pages/Sounds'
import PatreonOauth from './pages/Settings/PatreonOauth'
import { SagaDetail } from './pages/Saga/Detail'
import { SagaList } from './pages/Saga/List'
import { ImageSettingsModal } from './pages/Settings/Image/ImageSettings'
import { ResetPasswordPage } from './pages/Login/ResetPassword'
import { api } from './store/api'
import { GlobalFileInput } from './shared/GlobalFileInput'
import LoginModal from './pages/AiFans/LoginModal'
import ModelPage from './pages/AiFans/ModelPage'
import Header from './shared/aifans/Header'
import ChatLeftNav from './pages/AiFans/Chat/ChatLeftNav'
import ChatRecentChatsNav from './pages/AiFans/Chat/ChatRecentChatsNav'
import ChatRightNav from './pages/AiFans/Chat/ChatRightNav'
import RedirectToHomepage from './pages/AiFans/RedirectToHomepage'
import GenerateImage from './pages/AiFans/GenerateImage'
import Gallery from './pages/AiFans/Gallery'
import HeaderContainer from './shared/aifans/HeaderContainer'
import ChatLeftNavContainer from './pages/AiFans/Chat/ChatLeftNavContainer'
import BuyTokens from './pages/AiFans/BuyTokens/BuyTokens'
import ContactUs from './pages/AiFans/ContactUs'
import DmcaPolicy from './pages/AiFans/DmcaPolicy'
import PaymentHistory from './pages/AiFans/PaymentHistory'
import UserSettings from './pages/AiFans/UserSettings'
import SideMenuAndBottomNav from './shared/aifans/SideMenuAndBottomNav'
import { AppShell } from './shared/aifans/AppShell'
import Subscriptions from './pages/AiFans/Subscriptions'
import AiFansPolicy from './pages/AiFans/AiFansPolicy'
import CreatorFormModal from './pages/AiFans/CreatorFormModal'
import CharacterFormModal from './pages/AiFans/CharacterFormModal'
import AgeVerificationModal from './pages/AiFans/AgeVerificationModal'
import PaymentSuccess from './pages/AiFans/BuyTokens/PaymentSuccess'
import BuyTokensModal from './pages/AiFans/BuyTokens/BuyTokens'
import HentaiBuyTokensModal from './pages/AiFans/BuyTokens/HentaiBuyTokens'
import ResetPassword from './pages/ResetPassword'
import CharacterDirectory from './pages/HomePage/CharacterDirectory'
import SubscriptionCancelled from './pages/AiFans/SubscriptionCancelled'
import SubscriptionReactivated from './pages/AiFans/SubscriptionReactivated'
import AdminTransactions from './pages/AiFans/AdminTransactions'
import AdminSubscriptions from './pages/AiFans/AdminSubscriptions'
import { NoTokensModal } from './pages/AiFans/BuyTokens/NoTokensModal'
import { warmupManager } from './shared/aifans/WarmupManager'

// Client-side redirect: /chat → /live-chat (handles SPA in-app navigation)
const ChatRedirect: Component = () => {
  const params = useParams()
  const nav = useNavigate()
  nav(params.id ? `/live-chat/${params.id}` : '/live-chat', { replace: true })
  return null
}

const App: Component = () => {
  const state = userStore()
  const cfg = settingStore()

  // Handle tab visibility changes for warmup
  onMount(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && state.loggedIn) {
        // Tab became visible, trigger warmup (only for logged-in users)
        warmupManager.triggerWarmUp()
      }
    }

    // Listen for visibility changes (tab switching) - most reliable
    document.addEventListener('visibilitychange', handleVisibilityChange)

    // Cleanup on unmount
    onCleanup(() => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
    })
  })

  return (
    <MetaProvider>
      <Router root={Layout}>
        <Route path="/" component={HomePageAiFans} />
        <Route path="/hentai" component={lazy(() => import('./pages/AiFans/Hentai'))} />
        <Route
          path="/lp/candice-nice"
          component={lazy(() => import('./pages/AiFans/CandiceNiceLanding'))}
        />
        <Route
          path="/lp/hentai/1"
          component={lazy(() => import('./pages/AiFans/LandingPageHentai'))}
        />
        <Route path="/for-sale" component={lazy(() => import('./pages/AiFans/ForSaleLanding'))} />
        <Route
          path="/for-sale/success"
          component={lazy(() => import('./pages/AiFans/ForSaleLanding'))}
        />
        <Route
          path="/become-creator"
          component={lazy(() => import('./pages/AiFans/BecomeCreator'))}
        />
        <Route path="/requests" component={lazy(() => import('./pages/AiFans/Requests'))} />
        <Route path="/pricing" component={lazy(() => import('./pages/AiFans/Pricing'))} />
        <Route path="/directory" component={lazy(() => import('./pages/AiFans/Directory'))} />
        <Route path="/characters" component={lazy(() => import('./pages/AiFans/Characters'))} />
        <Route path="/model/:id" component={ModelPage} />
        <Route path="/model/:categorySlug/:id" component={ModelPage} />

        {/* Live Chat — public (logged-out users see the page, Send prompts login) */}
        <Route path="/live-chat" component={ChatDetail} />
        <Route path="/live-chat/:id" component={ChatDetail} />

        {/* Generate Image — public (logged-out users see the page, actions prompt login) */}
        <Route path="/generate-image/" component={GenerateImage} />
        <Route path="/generate-image/:id" component={GenerateImage} />
        {/* Client-side fallback redirects from old /chat URLs */}
        <Route path="/chat" component={ChatRedirect} />
        <Route path="/chat/:id" component={ChatRedirect} />

        <Route
          path="/terms-of-use"
          component={lazy(() => import('./pages/AiFans/TermsOfService'))}
        />
        <Route path="/checkout">
          <Route path="/success" component={CheckoutSuccess} />
          <Route path="/cancel" component={CheckoutCancel} />
        </Route>
        <Route
          path="/privacy-policy"
          component={(props) => (
            <AiFansPolicy
              {...props}
              contentId="3bBvgFpYcaroU0o5ibvWX0"
              showSidebar={false}
              canonical="/privacy-policy/"
            />
          )}
        />
        <Route
          path="/18-u-s-c-2257-record-keeping-requirements-exemption-statement"
          component={(props) => (
            <AiFansPolicy
              {...props}
              contentId="7FwCvddqK4mx20BhjrT4uT"
              showSidebar={false}
              canonical="/18-u-s-c-2257-record-keeping-requirements-exemption-statement/"
            />
          )}
        />
        <Route
          path="/content-removal"
          component={(props) => (
            <AiFansPolicy
              {...props}
              contentId="czISuaIzjOM4M0AI1qX48"
              showSidebar={false}
              canonical="/content-removal/"
              badge="Report a Concern"
              notice="All requests will be reviewed and resolved within 5 business days"
            />
          )}
        />
        <Show when={state.loggedIn}>
          <Route path="/gallery" component={Gallery} />
          <Route path="/following" component={lazy(() => import('./pages/AiFans/Following'))} />
          <Route
            path="/top-creators"
            component={lazy(() => import('./pages/AiFans/TopCreators'))}
          />
          <Route path="/buy-tokens" component={BuyTokens} />
          <Route path="/payment-history" component={PaymentHistory} />
          <Route path="/payment-history/:page" component={PaymentHistory} />
          <Route path="/settings" component={UserSettings} />
          <Route path="/rewards" component={lazy(() => import('./pages/AiFans/Rewards'))} />
          <Route path="/subscriptions" component={Subscriptions} />
          <Route path="/ogchat/:id" component={ChatDetail} />
          <Route path="/ogchat/" component={ChatDetail} />

          <Show when={state.user?.admin}>
            <Route path={['/info']} component={HomePage} />
            <Route
              path="/discord"
              component={() => <Redirect external="https://discord.agnai.chat" />}
            />

            <ScenarioRoutes />

            <ChubRoutes />
            <Route path="/chats/create/:id?" component={() => <CreateChatForm />} />
            <Route path="/chats" component={CharacterChats} />

            <Show when={cfg.config.guidanceAccess || state.user?.admin}>
              <CharacterRoutes />
              <Route path="/saga" component={SagaList} />
              <Route path="/saga/:id" component={SagaDetail} />
            </Show>

            <Route
              path="/presets/:id"
              component={lazy(() => import('./pages/GenerationPresets'))}
            />
            <Route
              path="/presets"
              component={lazy(() => import('./pages/GenerationPresets/PresetList'))}
            />
            <Show when={cfg.flags.sounds}>
              <Route path="/sounds" component={SoundsPage} />
            </Show>
            <Route path="/oauth/patreon" component={PatreonOauth} />
            <Route path="/profile" component={() => <ProfilePage />} />
            <Route path="/ogsettings" component={() => <Settings />} />
            <Route path="/memory" component={lazy(() => import('./pages/Memory/Library'))} />
            <Route
              path="/memory/:id"
              component={lazy(() => import('./pages/Memory/EditMemoryPage'))}
            />

            <Route path="/guides">
              <Route path="/memory" component={MemoryGuide} />
              <Route path="/novel" component={NovelGuide} />
            </Route>

            <Route path="/invites" component={lazy(() => import('./pages/Invite/InvitesPage'))} />
            <Route path="/admin/metrics" component={lazy(() => import('./pages/Admin/Metrics'))} />

            <Route
              path="/admin/configuration"
              component={lazy(() => import('./pages/Admin/Configuration'))}
            />

            <Route
              path="/admin/users"
              component={lazy(() => import('./pages/AiFans/AdminUsers'))}
            />
            <Route
              path="/admin/users/:page"
              component={lazy(() => import('./pages/AiFans/AdminUsers'))}
            />

            <Route
              path="/admin/subscriptions"
              component={lazy(() => import('./pages/Admin/SubscriptionList'))}
            />
            <Route
              path="/admin/subscriptions/:id"
              component={lazy(() => import('./pages/Admin/SubscriptionModel'))}
            />
            <Route
              path={['/admin/announcements', '/admin/announcements/:id']}
              component={lazy(() => import('./pages/Admin/Announcements'))}
            />
            <Route path="/admin/tiers/:id" component={lazy(() => import('./pages/Admin/Tiers'))} />
            <Route path="/admin/transactions" component={AdminTransactions} />
            <Route path="/admin/transactions/:page" component={AdminTransactions} />
            <Route path="/admin/subs" component={AdminSubscriptions} />
            <Route path="/admin/subs/:page" component={AdminSubscriptions} />
          </Show>
        </Show>
        <Show when={cfg.config.canAuth}>
          <Route path={['/login', '/login/remember']} component={LoginPage} />
          <Route path="/recovery" component={ResetPasswordPage} />
        </Show>
        <Route path="/faq" component={FAQ} />
        <Route path="/contact-us" component={ContactUs} />
        <Route path="/blog" component={lazy(() => import('./pages/AiFans/Blog'))} />
        <Route
          path="/blog/articles/:slug"
          component={lazy(() => import('./pages/AiFans/Blog/Article'))}
        />
        <Route path="/blog/tag/:tag" component={lazy(() => import('./pages/AiFans/Blog'))} />
        <Route path="/dmca-policy" component={DmcaPolicy} />
        <Route path="/payment-success" component={() => <PaymentSuccess type="tokens" />} />
        <Route
          path="/subscription-success"
          component={() => <PaymentSuccess type="subscription" />}
        />
        <Route path="/subscription-cancelled/:characterId" component={SubscriptionCancelled} />
        <Route path="/subscription-reactivated/:characterId" component={SubscriptionReactivated} />
        <Route path="/reset-password/:token" component={ResetPassword} />
        <Route path="*" component={RedirectToHomepage} />
      </Router>
    </MetaProvider>
  )
}

const IframeLayout: Component<{ children?: any }> = (props) => {
  const rootModals = rootModalStore()
  return (
    <ContextProvider>
      <div class="h-screen w-full">{props.children}</div>
      <LoginModal />
      <For each={rootModals.modals}>{(modal) => modal.element}</For>
    </ContextProvider>
  )
}

const Layout: Component<{ children?: any }> = (props) => {
  const location = useLocation()
  const state = userStore()

  const [isAdmin, setIsAdmin] = createSignal(false)

  // Update the isIframePage memo to include both payment routes
  const isIframePage = createMemo(() => {
    return (
      location.pathname.startsWith('/payment-success') ||
      location.pathname.startsWith('/subscription-success') ||
      location.pathname.startsWith('/subscription-cancelled') ||
      location.pathname.startsWith('/subscription-reactivated') ||
      location.pathname.startsWith('/lp') ||
      location.pathname.startsWith('/for-sale')
    )
  })

  // Return minimal layout for iframe pages
  if (isIframePage()) {
    return <IframeLayout>{props.children}</IframeLayout>
  }

  // Rest of the existing Layout code...
  const cfg = settingStore()
  const pane = usePaneManager()
  const params = useParams()

  const maxW = createMemo((): string => {
    if (pane.showing()) return 'max-w-full'

    return getMaxChatWidth(state.ui.chatWidth)
  })
  const rootModals = rootModalStore()

  const reload = () => {
    settingStore.init()
  }

  onMount(() => {
    settingStore.init()

    const params = new URLSearchParams(window.location.search)
    const showTokens = params.get('showTokens')
    if (showTokens === '1') {
      userStore.buyTokensModal(true)
      window.history.replaceState({}, '', window.location.pathname)
    } else if (showTokens === 'empty') {
      // #212: deep-link the out-of-tokens ("You're Running On Empty") popup.
      userStore.showNoTokensModal(true)
      window.history.replaceState({}, '', window.location.pathname)
    }
  })

  const isChat = createMemo(() => {
    if (location.pathname.startsWith('/chats')) {
      return false
    }

    return location.pathname.startsWith('/live-chat') || location.pathname.startsWith('/saga/')
  })

  createEffect(() => {
    setIsAdmin(!!state?.user?.admin)
  })

  const isAiFansPage = createMemo(() => {
    if (location.pathname.startsWith('/chats')) {
      return false
    }

    return (
      location.pathname === '/' ||
      location.pathname.startsWith('/model') ||
      location.pathname.startsWith('/live-chat') ||
      location.pathname.startsWith('/generate-image') ||
      location.pathname.startsWith('/gallery') ||
      location.pathname.startsWith('/buy-tokens') ||
      location.pathname.startsWith('/payment-history') ||
      location.pathname.startsWith('/faq') ||
      location.pathname.startsWith('/contact-us') ||
      location.pathname.startsWith('/dmca-policy') ||
      location.pathname.startsWith('/privacy-policy') ||
      location.pathname.startsWith('/content-removal') ||
      location.pathname.startsWith('/settings') ||
      location.pathname.startsWith('/rewards') ||
      location.pathname.startsWith('/subscriptions') ||
      location.pathname.startsWith('/reset-password') ||
      location.pathname.startsWith('/subscription-cancelled') ||
      location.pathname.startsWith('/subscription-reactivated') ||
      location.pathname.startsWith('/admin/transactions') ||
      location.pathname.startsWith('/admin/subs') ||
      location.pathname.startsWith('/admin/users') ||
      location.pathname.startsWith(
        '/18-u-s-c-2257-record-keeping-requirements-exemption-statement'
      ) ||
      location.pathname.startsWith('/terms-of-use') ||
      location.pathname.startsWith('/lp') ||
      location.pathname.startsWith('/hentai') ||
      location.pathname.startsWith('/characters') ||
      location.pathname.startsWith('/inbox') ||
      location.pathname.startsWith('/following') ||
      location.pathname.startsWith('/top-creators') ||
      location.pathname.startsWith('/requests') ||
      location.pathname.startsWith('/pricing') ||
      location.pathname.startsWith('/become-creator') ||
      location.pathname.startsWith('/directory') ||
      location.pathname.startsWith('/blog') ||
      location.pathname.startsWith('/tokens') ||
      location.pathname.startsWith('/exclusive-gallery')
    )
  })

  const isHomePage = createMemo(() => {
    return location.pathname === '/'
  })

  const bgStyles = useCharacterBg('layout')

  return (
    <>
      <Show when={isChat()}>
        <ContextProvider>
          <AppShell>
            {/* ChatLeftNavContainer removed — replaced by AppShell sidebar */}
            <div
              id="main"
              class="flex-grow md:grid md:grid-cols-[auto_1fr] xl:grid-cols-[auto_1fr_320px]"
            >
              <div
                class={`${
                  cfg.showLeftPanelMobile ? 'flex' : 'hidden'
                } h-[calc(100dvh-var(--cf-topbar-h)-var(--cf-tabbar-h))] flex-col overflow-y-auto sm:h-[calc(100dvh-var(--cf-topbar-h))] md:!flex md:h-[calc(100vh-82px)]`}
                style={{
                  background: 'var(--cf-surface)',
                  'border-right': '1px solid var(--cf-hairline)',
                }}
              >
                <ChatRecentChatsNav />
              </div>
              <div class={`${cfg.showLeftPanelMobile ? 'hidden' : 'flex'} flex-col md:!flex`}>
                {props.children}
              </div>
              {/* Right panel — permanent column at XL+ */}
              <Show when={params.id}>
                <div
                  class="hidden h-[calc(100vh-82px)] overflow-y-auto p-5 xl:block"
                  style={{
                    background: 'var(--cf-surface)',
                    'border-left': '1px solid var(--cf-hairline)',
                    'scrollbar-width': 'thin',
                  }}
                >
                  <ChatRightNav />
                </div>
              </Show>
            </div>
            {/* Character profile overlay — below XL only */}
            <Show when={cfg.showRightNav && params.id}>
              <div
                class="fixed bottom-0 right-0 z-[60] w-full max-w-[360px] overflow-y-auto p-5 xl:hidden"
                style={{
                  top: 'var(--cf-topbar-h)',
                  background: 'var(--cf-surface)',
                  'border-left': '1px solid var(--cf-hairline)',
                  'scrollbar-width': 'thin',
                }}
              >
                <ChatRightNav />
              </div>
              <button
                onClick={() => settingStore.rightNav(false)}
                class="fixed z-[61] grid h-9 w-9 place-items-center rounded-full text-[14px] text-white xl:hidden"
                style={{
                  top: 'calc(var(--cf-topbar-h) + 12px)',
                  right: '12px',
                  background: 'rgba(0,0,0,0.6)',
                }}
              >
                ×
              </button>
            </Show>
          </AppShell>
          <InfoModal />
          <ImageModal />
          <CreatorFormModal />
          <CharacterFormModal />
          <BuyTokensModal />
          <HentaiBuyTokensModal />
          <NoTokensModal />
          {/* Live Chat is now visible logged-out (#188): mount LoginModal so the
              send/character login-gate can actually render the login popup here.
              #208: LoginModal only *registers* the login popup into rootModals; the
              chat branch also needs the <For> renderer (present in the non-chat
              branch) or the popup is registered but never displayed on /live-chat. */}
          <LoginModal />
          <For each={rootModals.modals}>{(modal) => modal.element}</For>
          <Show
            when={
              !location.pathname.startsWith('/privacy-policy') &&
              !location.pathname.startsWith('/terms-of-use') &&
              !location.pathname.startsWith('/for-sale')
            }
          >
            <AgeVerificationModal />
          </Show>
          <Notifications />
          <CharacterDirectory
            show={state.showDirectory}
            close={() => userStore.directoryModal(false)}
          />
        </ContextProvider>
      </Show>

      <Show when={!isChat()}>
        <ContextProvider>
          <style>{css}</style>
          <Show
            when={isAiFansPage()}
            fallback={
              <div class="flex flex-col justify-between">
                <Show when={isAdmin()}>
                  <NavBar />
                </Show>
                <div class="flex w-full grow flex-row">
                  <Show when={isAdmin()}>
                    <Navigation />
                  </Show>
                  <main
                    id="main-content"
                    class={`w-full ${isAdmin() ? 'overflow-y-auto overflow-x-hidden' : ''}`}
                    classList={
                      isAdmin()
                        ? {
                            'sm:ml-[320px]': cfg.showMenu,
                            'sm:ml-0': !cfg.showMenu,
                          }
                        : {}
                    }
                    data-background
                    style={isAdmin() ? { ...bgStyles(), 'scrollbar-gutter': 'stable' } : {}}
                  >
                    <div class="content-background h-full min-h-full">
                      <Switch>
                        <Match when={cfg.init}>
                          {props.children}
                          <Maintenance />
                        </Match>
                        <Match when={cfg.initLoading}>
                          <div class="flex h-[80vh] flex-col items-center justify-center gap-2">
                            <Loading />
                          </div>
                        </Match>
                        <Match when>
                          <div class="flex flex-col items-center gap-2">
                            <div>CosplayFans failed to load</div>
                            <div>
                              <Button onClick={reload}>Try Again</Button>
                            </div>
                          </div>
                        </Match>
                      </Switch>
                    </div>
                  </main>
                </div>
              </div>
            }
          >
            <AppShell>
              <Switch>
                <Match when={cfg.init}>
                  {props.children}
                  <Maintenance />
                </Match>
                <Match when={cfg.initLoading}>
                  <div class="flex h-[80vh] flex-col items-center justify-center gap-2">
                    <Loading />
                  </div>
                </Match>
                <Match when>
                  <div class="flex flex-col items-center gap-2">
                    <div>CosplayFans failed to load</div>
                    <div>
                      <Button onClick={reload}>Try Again</Button>
                    </div>
                  </div>
                </Match>
              </Switch>
            </AppShell>
          </Show>
          <Notifications />
          <ImpersonateModal
            show={cfg.showImpersonate}
            close={() => settingStore.toggleImpersonate(false)}
          />
          <InfoModal />
          <ProfileModal />
          <BannedModal />
          <GlobalFileInput />
          <For each={rootModals.modals}>{(modal) => modal.element}</For>
          <ImageModal />
          <Show when={cfg.showImgSettings}>
            <ImageSettingsModal />
          </Show>
          <SettingsModal />
          <LoginModal />
          <CreatorFormModal />
          <CharacterFormModal />
          <BuyTokensModal />
          <HentaiBuyTokensModal />
          <NoTokensModal />
          <Show
            when={
              !location.pathname.startsWith('/privacy-policy') &&
              !location.pathname.startsWith('/terms-of-use')
            }
          >
            <AgeVerificationModal />
          </Show>
          <CharacterDirectory
            show={state.showDirectory}
            close={() => userStore.directoryModal(false)}
          />
          <div
            class="absolute bottom-0 left-0 right-0 top-0 z-10 h-[100vh] w-full bg-black bg-opacity-20 sm:hidden"
            classList={{ hidden: !cfg.showMenu }}
            onClick={() => settingStore.closeMenu()}
          ></div>
        </ContextProvider>
      </Show>
    </>
  )
}

const InfoModal: Component = (props) => {
  const state = rootModalStore()

  return (
    <Modal
      title={state.infoTitle || 'Information'}
      show={state.info}
      close={() => rootModalStore.closeInfo()}
      maxWidth="half"
    >
      <Show when={typeof state.info === 'string'} fallback={state.info}>
        <div class="markdown" innerHTML={markdown.makeHtml(state.info)} />
      </Show>
    </Modal>
  )
}

const BannedModal: Component = () => {
  const state = userStore((u) => ({
    banned: u.banned,
  }))

  return (
    <Modal show={!!state.banned} close={() => userStore.logout()} title="Account Banned">
      <div class="flex flex-col items-center">
        <p class="">This account has been banned for the following reason:</p>
        <p class="my-2 flex items-center font-bold">{state.banned}</p>
        <p>If you believe this is an error, please contact support.</p>
      </div>
    </Modal>
  )
}

export default App
